// src/components/separation/settlementslist.tsx
// Full & Final settlements list — replaces the old "coming soon" placeholder.

import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Wallet } from 'lucide-react';
import { FullFinalSettlement } from '../../types/separation';
import { getSettlements, settlementAction } from '../../api/separation';
import { statusBadge, employeeLabel, fmtMoney } from './uiHelpers';
import { useAccess } from '../../contexts/AccessContext';

interface SettlementsListProps {
  userRole?: string;
  onChanged?: () => void;
}

const SettlementsList: React.FC<SettlementsListProps> = ({ onChanged }) => {
  // Acting on a settlement = the "separation" panel at Manage in Access
  // Management; no role list.
  const { canManage } = useAccess();
  const canAct = canManage('separation');

  const [rows, setRows] = useState<FullFinalSettlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSettlements({ page, limit });
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('Failed to load settlements:', e);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (s: FullFinalSettlement, action: 'APPROVE' | 'MARK_PAID') => {
    setBusyId(s.id);
    try {
      await settlementAction(s.id, { action });
      await load();
      onChanged?.();
    } catch (e: any) {
      alert(e.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  if (loading && page === 1) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">Full &amp; Final Settlements</h3>
        <p className="text-xs text-gray-500 mt-0.5">Total: {total}</p>
      </div>

      <div className="overflow-auto max-h-[70vh]">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-500">
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Employee</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Type</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase text-right">Earnings</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase text-right">Deductions</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase text-right">Net Payable</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Status</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((s) => {
              const badge = statusBadge(s.status);
              return (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{employeeLabel(s)}</div>
                    <div className="text-xs text-gray-400">{s.employee_code || `#${s.id.slice(0, 8)}`}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-600 capitalize">{(s.separation_type || '').toLowerCase()}</td>
                  <td className="px-6 py-4 text-right text-gray-700">{fmtMoney(s.gross_earnings)}</td>
                  <td className="px-6 py-4 text-right text-red-600">{fmtMoney(s.total_deductions)}</td>
                  <td className="px-6 py-4 text-right font-semibold text-gray-900">{fmtMoney(s.net_payable)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.cls}`}>{badge.label}</span>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap space-x-3">
                    {canAct && (s.status === 'CALCULATED' || s.status === 'PENDING') && (
                      <button
                        onClick={() => act(s, 'APPROVE')}
                        disabled={busyId === s.id}
                        className="text-emerald-600 hover:text-emerald-800 font-medium disabled:opacity-50"
                      >
                        {busyId === s.id ? '…' : 'Approve'}
                      </button>
                    )}
                    {canAct && s.status === 'APPROVED' && (
                      <button
                        onClick={() => act(s, 'MARK_PAID')}
                        disabled={busyId === s.id}
                        className="text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50"
                      >
                        {busyId === s.id ? '…' : 'Mark Paid'}
                      </button>
                    )}
                    {s.status === 'PAID' && <span className="text-gray-400">Settled</span>}
                    {!canAct && s.status !== 'PAID' && <span className="text-gray-400">—</span>}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  <Wallet className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  No settlements yet. Create one from an approved resignation or a termination.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="gt-btn is-ghost is-sm disabled:opacity-50">
            <ChevronLeft size={14} /> Previous
          </button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="gt-btn is-ghost is-sm disabled:opacity-50">
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SettlementsList;
